import React, { useState } from "react";
import { getCsrfToken } from "../../../../public/api/authApi";
import { createCallLog, searchUsers } from "../../api/callLogsApi";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import {
  GlobalTextField,
  GlobalContactNumberField,
  GlobalSelectField,
  GlobalDateField,
  GlobalTextAreaField,
  GlobalButton,
} from "../../../../global/forms/GlobalFormFields";
import GlobalUserSearchField from "../../../../global/forms/GlobalUserSearchField";

const INITIAL_FORM = {
  caller_name: "",
  contact_number: "",
  call_type: "",
  purpose_id: "",
  is_call_completed: "",
  handled_by: "",
  call_duration: "",
  action_taken: "",
  remarks: "",
  follow_up_date: "",
};

const getLocalDateString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate()
  ).padStart(2, "0")}`;
};

const getCurrentTime = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
};

const sanitizeCallerName = (value) => String(value || "").replace(/[^a-zA-Z\s]/g, "");

const ReceptionistQuickCallLogForm = ({ masterData, onSuccess }) => {
  const [formData, setFormData] = useState({ ...INITIAL_FORM });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleFieldChange = (name) => (value) => {
    const nextValue = name === "caller_name" ? sanitizeCallerName(value) : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleCallStatusChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      is_call_completed: value,
      follow_up_date: value === "true" ? "" : prev.follow_up_date,
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.is_call_completed;
      if (value === "true") delete next.follow_up_date;
      return next;
    });
  };

  const resetForm = () => {
    setFormData({ ...INITIAL_FORM });
    setErrors({});
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.caller_name.trim()) nextErrors.caller_name = "Caller name is required";
    if (!/^[0-9]{10}$/.test(String(formData.contact_number || ""))) {
      nextErrors.contact_number = "Mobile number must be exactly 10 digits";
    }
    if (formData.call_type === "") nextErrors.call_type = "Call type is required";
    if (!formData.purpose_id) nextErrors.purpose_id = "Purpose is required";
    if (!formData.handled_by) nextErrors.handled_by = "Person to contact is required";
    const dur = Number(formData.call_duration);
    if (
      formData.call_duration === "" ||
      formData.call_duration == null ||
      !Number.isFinite(dur) ||
      dur < 1 ||
      dur > 300
    ) {
      nextErrors.call_duration = "Enter duration between 1 and 300 minutes";
    }
    if (formData.is_call_completed !== "true" && formData.is_call_completed !== "false") {
      nextErrors.is_call_completed = "Select call status";
    }
    if (formData.is_call_completed === "false" && !formData.follow_up_date) {
      nextErrors.follow_up_date = "Follow-up date is required when status is needs follow-up";
    }
    if (
      formData.is_call_completed === "false" &&
      formData.follow_up_date &&
      formData.follow_up_date < getLocalDateString()
    ) {
      nextErrors.follow_up_date = "Follow-up date cannot be before today";
    }
    return nextErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }
    setLoading(true);
    setErrors({});
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      const payload = {
        call_date: getLocalDateString(),
        call_time: getCurrentTime(),
        caller_name: formData.caller_name.trim(),
        contact_number: String(formData.contact_number).trim(),
        call_type: formData.call_type,
        purpose_id: formData.purpose_id,
        handled_by: formData.handled_by,
        is_call_completed: formData.is_call_completed === "true",
        call_duration: Number(formData.call_duration),
        action_taken: formData.action_taken || null,
        follow_up_date:
          formData.is_call_completed === "true" ? null : formData.follow_up_date || null,
        remarks: formData.remarks || "",
      };
      const result = await createCallLog(payload, csrfToken);
      if (result?.error) {
        setErrors({ api: result.error.message || "Failed to save call log" });
        return;
      }
      resetForm();
      onSuccess?.("Call logged successfully");
    } catch (err) {
      setErrors({ api: err.message || "Failed to save call log" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <GlobalFormShell
        legend="Quick Add Call"
        showAlert={Object.keys(errors).length > 0}
        alertText={errors.api || "Please fix the highlighted fields."}
        actions={
          <>
            <GlobalButton type="submit" variant="primary" disabled={loading}>
              {loading ? "Saving..." : "Save Call"}
            </GlobalButton>
            <GlobalButton type="button" variant="ghost" onClick={resetForm} disabled={loading}>
              Clear
            </GlobalButton>
          </>
        }
      >
        <p
          className="global-form-hint"
          style={{ gridColumn: "1 / -1", margin: "0 0 0.25rem", color: "#64748b", fontSize: "0.875rem" }}
        >
          Call date and time are saved automatically as today&apos;s date and the current time when you submit.
        </p>
        <GlobalTextField
          label="Caller Name *"
          value={formData.caller_name}
          onChange={handleFieldChange("caller_name")}
          error={errors.caller_name}
          placeholder="Enter caller name"
        />
        <GlobalContactNumberField
          label="Mobile Number *"
          value={formData.contact_number}
          onChange={handleFieldChange("contact_number")}
          error={errors.contact_number}
          placeholder="10-digit number"
        />
        <GlobalSelectField
          label="Call Type *"
          value={formData.call_type}
          onChange={handleFieldChange("call_type")}
          error={errors.call_type}
          options={(masterData?.types || []).map((t) => ({ value: String(t.id), label: t.type_name }))}
        />
        <GlobalSelectField
          label="Purpose *"
          value={formData.purpose_id}
          onChange={handleFieldChange("purpose_id")}
          error={errors.purpose_id}
          options={(masterData?.purposes || []).map((p) => ({ value: p.id, label: p.purpose_name }))}
        />
        <GlobalSelectField
          label="Call status *"
          value={formData.is_call_completed}
          onChange={handleCallStatusChange}
          error={errors.is_call_completed}
          options={[
            { value: "true", label: "Completed" },
            { value: "false", label: "Needs follow-up" },
          ]}
          placeholderOption="Select status"
        />
        <GlobalTextField
          label="Call duration (min) *"
          type="number"
          value={formData.call_duration}
          onChange={handleFieldChange("call_duration")}
          placeholder="1–300 minutes"
          min={1}
          max={300}
          step={1}
          error={errors.call_duration}
        />
        <GlobalUserSearchField
          variant="form"
          label="Person to Contact"
          required
          value={formData.handled_by}
          onChange={handleFieldChange("handled_by")}
          searchUsers={searchUsers}
          error={errors.handled_by}
          placeholder="Search name..."
        />
        <GlobalDateField
          label={`Follow-up date${formData.is_call_completed === "false" ? " *" : ""}`}
          value={formData.follow_up_date}
          onChange={handleFieldChange("follow_up_date")}
          disabled={formData.is_call_completed !== "false"}
          min={getLocalDateString()}
          error={errors.follow_up_date}
        />
        <GlobalTextAreaField
          label="Action Taken"
          value={formData.action_taken}
          onChange={handleFieldChange("action_taken")}
          rows={2}
          placeholder="Enter action taken"
        />
        <GlobalTextAreaField
          label="Remarks"
          value={formData.remarks}
          onChange={handleFieldChange("remarks")}
          rows={2}
        />
      </GlobalFormShell>
    </form>
  );
};

export default ReceptionistQuickCallLogForm;

